/* ============================================================
   Milano accessibility helper (2026-08-04)

   Fixes the P0 screen-reader flow from the 2026-08-03 audit:
   - A11Y-003 variant change: new price never announced
   - VAR-01   preorder state never announced
   - SR-01    focus destroyed when variant option markup is replaced
   - SR-04    add-to-cart success never announced
   - JRN-01   cart drawer: focus never moves in, Tab never enters,
              Esc returns focus to the header cart icon instead of
              the control that opened the drawer

   HARD RULES (see MILANO_A11Y_FIX_HANDOFF_2026-08-03.md):
   - READ-ONLY towards price/cart markup. Never writes to
     [data-product-price], [data-compare-price] or any [data-la-dn-*]
     element - Discount Ninja owns displayed prices.
   - Announces through the ONE polite region #milano-a11y-status
     (snippets/a11y-live-region.liquid), text only.
   - Event-driven only, no polling. Every handler fails silently -
     an a11y helper must never break checkout.
   ============================================================ */
(function () {
  'use strict';

  if (window.__milanoA11y) return;
  window.__milanoA11y = true;

  var STATUS_ID = 'milano-a11y-status';
  var ANNOUNCE_DEBOUNCE_MS = 350; // DN + product-info produce ~10 price mutations per change
  var lastAnnounced = '';

  function statusEl() {
    return document.getElementById(STATUS_ID);
  }

  function strings() {
    var el = statusEl();
    function attr(name, fallback) {
      var v = el && el.getAttribute(name);
      return v || fallback;
    }
    return {
      addToCart: attr('data-str-add-to-cart', 'Add to Cart'),
      inStock: attr('data-str-in-stock', 'Item is in stock'),
      added: attr('data-str-added', 'Added to cart'),
      price: attr('data-str-price', 'Price'),
      lightbox: attr('data-str-lightbox', 'Open image lightbox'),
      quickView: attr('data-str-quick-view', 'Quick Buy')
    };
  }

  function normalize(text) {
    return (text || '').replace(/\s+/g, ' ').trim();
  }

  function setStatusIn(el, text) {
    if (!el || !text) return;
    // aria-atomic region: identical text would not re-announce, so nudge
    // repeats with a trailing no-break space (invisible, but a new DOM value).
    if (el.textContent === text) text += '\u00A0';
    el.textContent = text;
    lastAnnounced = normalize(text);
  }

  function setStatus(text) {
    setStatusIn(statusEl(), text);
  }

  // Announce only when the message changed (variant announcements repeat a lot).
  function announceIfChanged(text) {
    text = normalize(text);
    if (!text || text === lastAnnounced) return;
    setStatus(text);
  }

  /* ------------------------------------------------------------
     1. Variant change -> announce selection, price, availability.
        Reads the *rendered* DOM after a debounce so Discount Ninja
        and product-info have finished their rewrites.
     ------------------------------------------------------------ */
  var announceTimer = null;
  var pendingProductInfo = null;

  var variantAnnouncePending = false;

  function scheduleVariantAnnounce(sourceEl) {
    var pi = sourceEl && sourceEl.closest ? sourceEl.closest('product-info') : null;
    if (pi) pendingProductInfo = pi;
    variantAnnouncePending = true;
    if (announceTimer) clearTimeout(announceTimer);
    announceTimer = setTimeout(announceVariantState, ANNOUNCE_DEBOUNCE_MS);
  }

  function onOptionValueSelectionChange(payload) {
    try {
      var evt = payload && payload.data && payload.data.event;
      scheduleVariantAnnounce(evt && evt.target);
    } catch (e) { /* fail silently */ }
  }

  // Clicking the ALREADY-selected option fires no change event, so nothing
  // announced the CURRENT price - a blind shopper had no way to hear the
  // starting state (merchant QA round 2). Any click on an option input
  // schedules an announce; the debounce + dedupe absorb the overlap with
  // the normal change flow.
  document.addEventListener('click', function (e) {
    try {
      var t = e.target;
      if (!t || !t.closest) return;
      var input = t.closest('[data-single-option-selector]');
      if (input) scheduleVariantAnnounce(input);
    } catch (err) { /* fail silently */ }
  });

  function selectedOptionText(pi) {
    var values = [];
    var seenNames = {};
    // One value per option NAME: the personalize wizard keeps a duplicate set
    // of option markup in the DOM, which read as "13" Victoria, Zig Zag Lace,
    // 13" Victoria, Zig Zag Lace" (merchant QA 2026-08-05).
    pi.querySelectorAll('[data-single-option-selector]:checked').forEach(function (input) {
      var name = input.getAttribute('name') || '';
      if (name && seenNames[name]) return;
      seenNames[name] = true;
      var v = normalize(input.value);
      if (v) values.push(v);
    });
    // Dropdown-style option values. The quantity picker is also a select-popout
    // ([data-quantity-select]) — exclude it or every announcement reads a stray
    // "1". indexOf guard: popouts and "selected" chips can echo a value the
    // radios already contributed.
    pi.querySelectorAll('.select-popout__value:not([data-quantity-select])').forEach(function (el) {
      var v = normalize(el.textContent);
      if (v && values.indexOf(v) === -1) values.push(v);
    });
    return values.join(', ');
  }

  function priceText(pi) {
    // Prefer the main price block; the ATC button can also carry a
    // [data-product-price] span when "show price in button" is on.
    var el = pi.querySelector('.product__price-wrapper [data-product-price]') ||
             pi.querySelector('[data-product-price]');
    if (!el) return '';
    // Discount Ninja may decorate the price span with the original price
    // struck through next to the sale price; raw textContent would then read
    // two prices back to back ("$1,300.00 $1,200.00"). Strip strikethrough
    // nodes from a clone so only the current price is announced. READ-ONLY:
    // the live DOM is never touched.
    var clone = el.cloneNode(true);
    clone.querySelectorAll('s, del, [data-compare-price]').forEach(function (n) { n.remove(); });
    return normalize(clone.textContent);
  }

  function availabilityText(pi, s) {
    var textEl = pi.querySelector('[data-add-to-cart-text]');
    if (!textEl) return '';
    // Clone and strip the nested price span so only the state word remains
    // ("Add to Cart" / "Pre-Order" / "Sold Out").
    var clone = textEl.cloneNode(true);
    clone.querySelectorAll('[data-product-price]').forEach(function (n) { n.remove(); });
    var state = normalize(clone.textContent);
    if (!state) return '';
    if (state.toLowerCase() === normalize(s.addToCart).toLowerCase()) return s.inStock;
    return state;
  }

  function announceVariantState() {
    try {
      variantAnnouncePending = false;
      // pendingProductInfo can be detached by a full product swap
      // (HTMLUpdateUtility replaces the whole product-info element) - fall
      // back to the live element instead of silently skipping the announcement.
      var pi = pendingProductInfo;
      if (!pi || !document.contains(pi)) pi = document.querySelector('product-info');
      if (!pi) return;
      var s = strings();
      var parts = [];
      var options = selectedOptionText(pi);
      if (options) parts.push(options + '.');
      var price = priceText(pi);
      if (price) parts.push(s.price + ' ' + price + '.');
      var availability = availabilityText(pi, s);
      if (availability) parts.push(availability + '.');
      announceIfChanged(parts.join(' '));
    } catch (e) { /* fail silently */ }
  }

  /* ------------------------------------------------------------
     2. Focus restoration after variant option markup is replaced.
        product-info.js re-focuses via querySelector('#' + id), which
        throws on ids containing spaces (e.g. "...-Lace Front-yes"),
        so focus falls to <body>. Track the last focused option input
        and put focus back on its replacement. Runs BEFORE the
        announcement debounce fires - moving focus mid-utterance
        truncates speech.
     ------------------------------------------------------------ */
  var lastOptionFocus = null;

  document.addEventListener('focusin', function (e) {
    try {
      var t = e.target;
      if (!t || !t.matches) return;
      if (t.matches('[data-single-option-selector]')) {
        lastOptionFocus = { kind: 'radio', id: t.id, name: t.getAttribute('name'), value: t.value };
      } else if (t.matches('[data-popout-toggle][aria-controls]')) {
        // Dropdown-style options (e.g. Cap Size) are popout toggles, not
        // radios; their aria-controls id is stable across re-renders.
        lastOptionFocus = { kind: 'popout', controls: t.getAttribute('aria-controls') };
      }
    } catch (err) { /* fail silently */ }
  });

  document.addEventListener('milano:variant-selects-replaced', function () {
    try {
      // The new markup (and price) just landed. Restart the announce debounce
      // from here — a fixed timer from the option click can fire before a slow
      // network render and read the OLD price (then dedupe would suppress the
      // real announcement entirely). announceTimer is intentionally never
      // nulled after firing: once the user has interacted with an option, any
      // later variant-markup replacement warrants a (re)announcement; before
      // the first interaction it is null and page-load renders stay silent.
      if (announceTimer) {
        clearTimeout(announceTimer);
        announceTimer = setTimeout(announceVariantState, ANNOUNCE_DEBOUNCE_MS);
      }
      if (!lastOptionFocus) return;
      // Delayed 120ms: the theme + wizard run their own post-render passes on
      // requestAnimationFrame (cap-size relocation etc.) that can move focus
      // AFTER an immediate restore (merchant QA round 2: focus landed on the
      // "Adjust Ponytail Cap Size" group). Restoring after those passes wins,
      // and still runs well before the 350ms announcement.
      setTimeout(function () {
        try {
          var active = document.activeElement;
          var focusHeld = active && active !== document.body &&
            active !== document.documentElement && document.contains(active) &&
            (active.matches('[data-single-option-selector]') ||
             active.matches('[data-popout-toggle]'));
          if (focusHeld) return; // focus survived on an option control - leave it
          var el = null;
          if (lastOptionFocus.kind === 'popout') {
            if (lastOptionFocus.controls && window.CSS && CSS.escape) {
              el = document.querySelector(
                '[data-popout-toggle][aria-controls="' + CSS.escape(lastOptionFocus.controls) + '"]'
              );
            }
          } else {
            el = lastOptionFocus.id ? document.getElementById(lastOptionFocus.id) : null;
            if (!el && lastOptionFocus.name && window.CSS && CSS.escape) {
              el = document.querySelector(
                '[data-single-option-selector][name="' + CSS.escape(lastOptionFocus.name) +
                '"][value="' + CSS.escape(lastOptionFocus.value) + '"]'
              );
            }
          }
          if (el) el.focus({ preventScroll: true });
        } catch (e) { /* fail silently */ }
      }, 120);
    } catch (e) { /* fail silently */ }
  });

  /* ------------------------------------------------------------
     3. Add to cart -> announce. theme.js dispatches theme:cart:added
        after the drawer markup is rebuilt, so the item count is
        already current. Delayed so the drawer focus move (below)
        happens first - a focus move would truncate the utterance.
        CRITICAL (merchant QA round 2): aria-modal on the open drawer
        makes screen readers ignore live regions OUTSIDE it - so when
        the drawer is open, announce through the region INSIDE it
        ([data-milano-cart-status] in cart-drawer.liquid).
     ------------------------------------------------------------ */
  document.addEventListener('theme:cart:added', function () {
    try {
      setTimeout(function () {
        try {
          var s = strings();
          var msg = s.added + '.';
          var qty = document.querySelector('#cart-drawer [data-cart-items-qty]');
          var qtyText = normalize(qty && qty.textContent);
          if (qtyText) msg += ' ' + qtyText + '.';
          var drawer = cartDrawer();
          var drawerRegion = drawer && drawer.classList.contains('is-open') &&
            drawer.querySelector('[data-milano-cart-status]');
          if (drawerRegion) {
            setStatusIn(drawerRegion, msg);
          } else {
            setStatus(msg); // always announce, even for repeat adds
          }
        } catch (e) { /* fail silently */ }
      }, 1000); // merchant QA 2026-08-05: at 500ms VoiceOver's own dialog +
                // list announcements for the opening drawer talked over this;
                // 1s lands after that speech settles
    } catch (e) { /* fail silently */ }
  });

  /* ------------------------------------------------------------
     4. Cart drawer focus management.
        theme.js calls a11y.trapFocus while the drawer is still
        hidden, so its focusable list is empty and the trap never
        engages (merchant-confirmed: Tab never enters). We:
        - record the real opener and hand it to theme.a11y.state.trigger
          so the theme's own close-restore aims at the right element
        - move focus onto the drawer once it is visible
        - run a live-computed Tab trap while it is open
        - restore focus to the opener on close if the theme didn't
     ------------------------------------------------------------ */
  var drawerOpener = null;
  var drawerCloseRestoreTimer = null;
  var lastOutsidePointerDownTs = 0;

  function cartDrawer() {
    return document.getElementById('cart-drawer');
  }

  // Track pointer interactions outside the open drawer: when the drawer is
  // closed by clicking elsewhere, the click's own focus outcome must win —
  // restoring focus to the opener would yank it from where the user pointed.
  document.addEventListener('mousedown', function (e) {
    try {
      var drawer = cartDrawer();
      if (drawer && drawer.classList.contains('is-open') &&
          e.target instanceof Node && !drawer.contains(e.target)) {
        lastOutsidePointerDownTs = Date.now();
      }
    } catch (err) { /* fail silently */ }
  });

  document.addEventListener('theme:cart:open', function () {
    try {
      // A reopen within the close-restore window must cancel the pending
      // restore, or the stale timer would yank focus out of the open dialog.
      if (drawerCloseRestoreTimer) {
        clearTimeout(drawerCloseRestoreTimer);
        drawerCloseRestoreTimer = null;
      }
      var opener = document.activeElement;
      if (opener && opener !== document.body && opener !== document.documentElement) {
        drawerOpener = opener;
        if (window.theme && window.theme.a11y && window.theme.a11y.state) {
          window.theme.a11y.state.trigger = opener;
        }
      }
      var drawer = cartDrawer();
      if (!drawer) return;
      setTimeout(function () {
        try {
          if (!drawer.classList.contains('is-open')) return;
          var active = document.activeElement;
          if (active && drawer.contains(active) && active !== drawer) return; // already inside
          // Prefer refreshing the theme's own trap: theme.js snapshotted its
          // firstFocusable/lastFocusable while the drawer was still hidden
          // (empty list), so re-running it now that elements are visible makes
          // both traps agree. Focus lands on the drawer container either way.
          if (window.theme && window.theme.a11y && window.theme.a11y.trapFocus) {
            window.theme.a11y.trapFocus({ container: drawer, elementToFocus: drawer });
          } else {
            drawer.focus({ preventScroll: true });
          }
        } catch (e) {
          try { cartDrawer().focus({ preventScroll: true }); } catch (e2) { /* fail silently */ }
        }
      }, 250); // after the open transition + Discount Ninja re-decoration
    } catch (e) { /* fail silently */ }
  });

  function visibleFocusables(container) {
    var nodes = container.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex^="-"])'
    );
    return Array.prototype.filter.call(nodes, function (el) {
      if (el.disabled || el.getAttribute('aria-hidden') === 'true') return false;
      return el.offsetWidth > 0 || el.offsetHeight > 0 || el.getClientRects().length > 0;
    });
  }

  document.addEventListener('keydown', function (e) {
    try {
      if (e.key !== 'Tab') return;
      var drawer = cartDrawer();
      if (!drawer || !drawer.classList.contains('is-open')) return;
      var focusables = visibleFocusables(drawer);
      if (!focusables.length) {
        e.preventDefault();
        drawer.focus({ preventScroll: true });
        return;
      }
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      var active = document.activeElement;
      if (!drawer.contains(active)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
        return;
      }
      if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && (active === first || active === drawer)) {
        e.preventDefault();
        last.focus();
      }
    } catch (err) { /* fail silently */ }
  });

  document.addEventListener('theme:cart:close', function () {
    try {
      var opener = drawerOpener;
      drawerOpener = null;
      if (!opener || !document.contains(opener)) return;
      // Closed by clicking outside the drawer? The pointer decides where focus
      // goes — do not steal it back to the opener.
      if (Date.now() - lastOutsidePointerDownTs < 400) return;
      if (drawerCloseRestoreTimer) clearTimeout(drawerCloseRestoreTimer);
      drawerCloseRestoreTimer = setTimeout(function () {
        try {
          drawerCloseRestoreTimer = null;
          var drawer = cartDrawer();
          if (drawer && drawer.classList.contains('is-open')) return; // reopened
          var active = document.activeElement;
          var focusLost = !active || active === document.body ||
            active === document.documentElement ||
            (drawer && drawer.contains(active));
          if (!focusLost) return; // theme (or the user) already placed focus
          opener.focus({ preventScroll: true });
        } catch (e) { /* fail silently */ }
      }, 100);
    } catch (e) { /* fail silently */ }
  });

  /* ------------------------------------------------------------
     5. Name the SC Product Options (Bold) radio groups.
        The app renders .bold_option rows client-side with labelled
        radios but no group name. Attributes only - never restructure
        app markup (see handoff constraint 2.4).
     ------------------------------------------------------------ */
  function nameBoldGroups() {
    try {
      // Both layouts: flat SC rows (.bold_option) AND the wizard's transformed
      // rows (.milano-option-row) - often the same element wearing both
      // classes, hence one combined pass (merchant QA round 3: wizard rows
      // were unnamed because only .bold_option was matched).
      document.querySelectorAll('.bold_options .bold_option, .milano-option-row').forEach(function (group) {
        if (!group.querySelector('input[type="radio"]')) return;
        if (!group.getAttribute('role')) {
          var title = '';
          var titleEl = group.querySelector('.bold_option_title');
          if (titleEl) {
            // Milano JS injects "What's This?" / "Compare" controls into titles -
            // strip them so the group name is just the option name.
            var clone = titleEl.cloneNode(true);
            clone.querySelectorAll('a, button').forEach(function (n) { n.remove(); });
            title = normalize(clone.textContent);
          }
          // Wizard rows carry the group name as data-option-name.
          if (!title) title = normalize(group.getAttribute('data-option-name'));
          if (title) {
            group.setAttribute('role', 'radiogroup');
            group.setAttribute('aria-label', title);
          }
        }
        // Each choice announces its visible text INCLUDING the price
        // ("Full Rooting (All Over) +$175.00") - the app's native labeling
        // left the price silent (merchant QA round 2). Attributes only.
        group.querySelectorAll('input[type="radio"]').forEach(function (input) {
          if (input.getAttribute('aria-label')) return;
          var holder = input.closest('.bold_option_value') || input.closest('label');
          if (!holder) return;
          var hClone = holder.cloneNode(true);
          hClone.querySelectorAll('input, select, textarea').forEach(function (n) { n.remove(); });
          var text = normalize(hClone.textContent);
          if (text) input.setAttribute('aria-label', text);
        });
      });
    } catch (e) { /* fail silently */ }
  }

  /* Entry-choice error: the wizard shows #mw-entry-required ("Please choose
     one...") and refocuses the first Yes option in the same beat, so its own
     role=alert loses the race against VoiceOver's focus speech (merchant QA
     round 3). Re-announce it through the live region after that speech. */
  var lastEntryError = '';

  function announceEntryError() {
    try {
      var el = document.getElementById('mw-entry-required');
      if (!el || el.offsetParent === null) { lastEntryError = ''; return; }
      var txt = normalize(el.textContent);
      if (!txt || txt === lastEntryError) return;
      lastEntryError = txt;
      setTimeout(function () {
        try { announceIfChanged(txt); } catch (e) { /* fail silently */ }
      }, 800);
    } catch (e) { /* fail silently */ }
  }

  /* Wizard total: picking a priced service updates the summary total but the
     app never announced it (merchant QA round 2). Baseline on first sight so
     page load stays silent; announce only real changes, and stand down when a
     variant announcement is already in flight (it carries the price). */
  var lastWizardTotal = null;

  function announceWizardTotal() {
    try {
      // Itemized (2026-08-06): the summary footer can carry an Options line
      // (setup surcharges) and an Added-services line - announce both, e.g.
      // "Options +$100. Added services +$175." Falls back to the wizard's
      // running services chip (.mw-wiz-total, services-only) while mid-flow.
      var parts = [];
      document.querySelectorAll('.milano-summary__total').forEach(function (line) {
        var spans = line.querySelectorAll('span');
        var t = spans.length >= 2
          ? normalize(spans[0].textContent) + ' ' + normalize(spans[spans.length - 1].textContent)
          : normalize(line.textContent);
        if (t) parts.push(t);
      });
      var txt = parts.join('. ');
      if (!txt) {
        var chip = document.querySelector('.mw-wiz-total');
        if (chip && chip.offsetParent !== null) {
          var delta = normalize(chip.textContent);
          if (delta) txt = 'Added services total ' + delta;
        }
      }
      if (!txt) return;
      if (lastWizardTotal === null) { lastWizardTotal = txt; return; }
      if (txt === lastWizardTotal) return;
      lastWizardTotal = txt;
      if (variantAnnouncePending) return; // variant announce in flight carries the price
      announceIfChanged(txt + '.');
    } catch (e) { /* fail silently */ }
  }

  // Announce the total after ANY wizard row interaction. Two triggers because
  // the wizard selects in two ways: native inputs fire `change` (Secure step),
  // while its custom cards update inputs programmatically - no change event -
  // so `click` delegation covers those (merchant QA: totals spoke for Secure
  // but not the other groups). announceWizardTotal's baseline + dedupe keep
  // this quiet unless the number actually changed.
  function scheduleWizardTotalAnnounce(e) {
    try {
      var t = e.target;
      if (!t || !t.closest) return;
      if (!t.closest('.milano-option-row, .bold_options .bold_option')) return;
      setTimeout(announceWizardTotal, 500);
    } catch (err) { /* fail silently */ }
  }
  document.addEventListener('change', scheduleWizardTotalAnnounce);
  document.addEventListener('click', scheduleWizardTotalAnnounce);

  var boldObserver = null;

  function watchBoldGroups() {
    try {
      if (!('MutationObserver' in window)) return;
      var root = document.querySelector('product-info') || document.querySelector('.product__form');
      if (!root) return;
      var scheduled = false;
      boldObserver = new MutationObserver(function () {
        if (scheduled) return;
        scheduled = true;
        setTimeout(function () {
          scheduled = false;
          nameBoldGroups();
          announceWizardTotal();
          announceEntryError();
          // Review/summary panel: labeled region so it's rotor-navigable
          // (auto-reading its full text would be far too verbose).
          var summary = document.querySelector('.milano-summary');
          if (summary && !summary.getAttribute('role')) {
            summary.setAttribute('role', 'region');
            summary.setAttribute('aria-label', 'Your wig summary');
          }
        }, 100);
      });
      boldObserver.observe(root, { childList: true, subtree: true });
      nameBoldGroups();
    } catch (e) { /* fail silently */ }
  }

  /* ------------------------------------------------------------
     6. Name the shared PhotoSwipe dialog. Container 0 serves both
        the product image lightbox and the Quick Buy popup, so the
        label is set per-open from the trigger that launched it.
     ------------------------------------------------------------ */
  function labelPswpDialog(label) {
    var el = document.querySelector('.pswp[data-index="0"]');
    if (el && label) el.setAttribute('aria-label', label);
  }

  document.addEventListener('click', function (e) {
    try {
      var t = e.target;
      if (!t || !t.closest) return;
      if (t.closest('[data-zoom-wrapper]')) {
        labelPswpDialog(strings().lightbox);
      } else if (t.closest('[data-button-quick-view]')) {
        labelPswpDialog(strings().quickView);
      }
    } catch (err) { /* fail silently */ }
  });

  /* ------------------------------------------------------------
     6a. Gallery trigger names. Liquid numbers the lightbox links by
         media.position (position in the FULL media list), which reads
         "1, 2, 22, 18" on Grace where the gallery filters to the
         selected variant. Renumber the VISIBLE triggers "n of total"
         and append the image's existing alt so a blind shopper knows
         what each one opens. Reads media alts, never writes them
         (Grace constraint). Progressive: no JS = Liquid numbering.
     ------------------------------------------------------------ */
  function renumberGalleryTriggers() {
    try {
      var base = strings().lightbox;
      // Per media group: the PDP gallery and an open Quick Buy each carry
      // their own [data-product-single-media-group] - numbering must not
      // run across both.
      document.querySelectorAll('[data-product-single-media-group]').forEach(function (group) {
        var visible = Array.prototype.filter.call(
          group.querySelectorAll('[data-zoom-wrapper]'),
          function (el) { return el.offsetParent !== null; }
        );
        if (!visible.length) return;
        visible.forEach(function (link, i) {
          var span = link.querySelector('.visually-hidden');
          if (!span) return;
          var media = link.closest('[data-product-single-media-wrapper]');
          var img = media && media.querySelector('img');
          var alt = normalize(img && img.getAttribute('alt'));
          var text = base + ' ' + (i + 1) + ' of ' + visible.length;
          if (alt) text += ' — ' + alt;
          if (span.textContent !== text) span.textContent = text;
        });
      });
    } catch (e) { /* fail silently */ }
  }

  document.addEventListener('milano:variant-selects-replaced', function () {
    // after the Grace gallery filter's post-render rAF pass
    setTimeout(renumberGalleryTriggers, 250);
  });
  document.addEventListener('theme:product-info:loaded', function () {
    setTimeout(renumberGalleryTriggers, 250);
  });

  /* ------------------------------------------------------------
     6b. Wizard step navigation. Continue/Back/step-pill clicks change
         the visible step but leave focus stranded on the button
         (merchant QA round 2) - a blind shopper never reaches the new
         question. After the wizard's own transition, move focus to
         the first choice of the newly visible step; with the group
         labels above, VoiceOver then announces the question context.
     ------------------------------------------------------------ */
  document.addEventListener('click', function (e) {
    try {
      var t = e.target;
      if (!t || !t.closest) return;
      var navBtn = t.closest('.mw-nav__next, .mw-nav__back, .mw-pill, .mw-review-card__edit, .mw-nav__skip');
      if (!navBtn) return;
      // "✓ Review My Wig" and "Skip to summary" land on the ADD TO CART button
      // (the review step's action) and speak a concise summary composed from
      // the review card's own lines - display and speech can never disagree
      // (merchant decision 2026-08-07).
      var toReview = navBtn.classList.contains('mw-nav__skip') ||
        (navBtn.classList.contains('mw-nav__next') && /review/i.test(navBtn.textContent || ''));
      setTimeout(function () {
        try {
          if (toReview) {
            var atc = document.querySelector('product-info form[action*="/cart/add"] [data-add-to-cart]') ||
                      document.querySelector('[data-add-to-cart]');
            if (atc) atc.focus();
            setTimeout(function () {
              try {
                var parts = [];
                document.querySelectorAll('.mw-review-card__line, .mw-review-card__total-line').forEach(function (line) {
                  var lt = normalize(line.textContent);
                  if (lt) parts.push(lt);
                });
                if (parts.length) announceIfChanged('Your wig is ready. ' + parts.join('. ') + '.');
              } catch (err) { /* fail silently */ }
            }, 900); // queue behind VoiceOver's focus speech for the button
            return;
          }
          // Merchant preference (QA round 4): after Continue/Back land focus on
          // the ACTIVE STEP PILL - the user hears where they are and chooses to
          // enter the group or keep moving through categories. Fallback: first
          // visible option row.
          var pill = document.querySelector('.mw-pill[aria-current="step"]');
          if (pill) { pill.focus({ preventScroll: true }); return; }
          var rows = document.querySelectorAll('.milano-option-row');
          if (!rows.length) rows = document.querySelectorAll('.bold_options .bold_option');
          for (var i = 0; i < rows.length; i++) {
            var row = rows[i];
            if (row.offsetWidth === 0 && row.offsetHeight === 0) continue; // hidden step
            var target = row.querySelector('input:not([type="hidden"]), select, textarea, button');
            if (target) { target.focus({ preventScroll: true }); return; }
          }
        } catch (err) { /* fail silently */ }
      }, 450); // after the wizard's step transition/enter animation
    } catch (err) { /* fail silently */ }
  });

  /* ------------------------------------------------------------
     6b2. Wizard intro (merchant-approved 2026-08-06; Esc-to-exit
     removed per merchant 2026-08-07). The panel's existing intro copy
     is announced ONCE per page when "Yes" is first chosen - the moment
     reassurance matters.
     ------------------------------------------------------------ */
  var wizardIntroAnnounced = false;

  document.addEventListener('click', function (e) {
    try {
      var t = e.target;
      if (!t || !t.closest || !t.closest('.mw-segmented__cell--personalize')) return;
      if (wizardIntroAnnounced) return;
      wizardIntroAnnounced = true;
      // 2.6s: lands after the step-1 announcement (900ms + speech) so the
      // polite region doesn't cut it off mid-utterance.
      setTimeout(function () {
        try {
          announceIfChanged('Personalization opened. Choose only the details you want to add — everything is optional and guided step by step.');
        } catch (err) { /* fail silently */ }
      }, 2600);
    } catch (err) { /* fail silently */ }
  });

  /* ------------------------------------------------------------
     6c. Wizard step context. personalize-wizard.js dispatches
         milano:wizard:step on every user-visible step change; announce
         "Step N of M: {label}." AFTER the focus move (450ms, above) and
         VoiceOver's own speech for the newly focused choice have begun -
         the polite region queues behind them, so nothing is truncated.
     ------------------------------------------------------------ */
  document.addEventListener('milano:wizard:step', function (e) {
    try {
      var d = e && e.detail;
      if (!d || !d.label || !d.index || !d.total) return;
      var msg = 'Step ' + d.index + ' of ' + d.total + ': ' + d.label + '.';
      setTimeout(function () {
        try { announceIfChanged(msg); } catch (err) { /* fail silently */ }
      }, 900);
    } catch (err) { /* fail silently */ }
  });

  /* ------------------------------------------------------------
     Theme editor: a section reload replaces the product-info subtree,
     detaching the Bold observer's root and any cached element refs.
     Re-arm so editor QA matches storefront behavior. (The event only
     fires inside the theme editor.)
     ------------------------------------------------------------ */
  document.addEventListener('shopify:section:load', function () {
    try {
      pendingProductInfo = null;
      if (boldObserver) {
        boldObserver.disconnect();
        boldObserver = null;
      }
      watchBoldGroups();
    } catch (e) { /* fail silently */ }
  });

  /* ------------------------------------------------------------
     Init
     ------------------------------------------------------------ */
  function init() {
    try {
      if (window.subscribe && window.theme && window.theme.PUB_SUB_EVENTS &&
          window.theme.PUB_SUB_EVENTS.optionValueSelectionChange) {
        window.subscribe(
          window.theme.PUB_SUB_EVENTS.optionValueSelectionChange,
          onOptionValueSelectionChange
        );
      }
    } catch (e) { /* fail silently */ }
    watchBoldGroups();
    // Initial gallery numbering: once at product-info load (listener above)
    // and once late, after Grace's first-paint variant filter settles.
    setTimeout(renumberGalleryTriggers, 1500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
